/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/class/Timer.ts
 * @create:      2023-11-27 11:29:26.635
 * @modify:      2025-06-29 08:25:58.353
 * @version:     6.0.0
 * @times:       13
 * @lines:       209
 * @copyright:   Copyright © 2023-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { TimeSpan } from "./TimeSpan";
import { KavenLiteEvent } from "./KavenLiteEvent";
import { DateTime } from "./DateTime";


/**
 * Represents a timer that can be used to schedule recurring tasks
 * with a specified time interval.
 */
export class Timer {
    /**
     * The time interval between each execution of the timer.
     */
    protected interval: TimeSpan;

    /**
     * Event that is triggered on each elapsed interval of the timer.
     */
    protected readonly elapsed = new KavenLiteEvent<number, Timer>();

    /**
     * Index representing the number of times the timer has elapsed.
     */
    protected index = 0;

    /**
     * Identifier of the currently scheduled timeout.
     */
    protected timeoutID?: string | number | NodeJS.Timeout | undefined;

    // Dates to keep track of timer execution
    protected startDateTime?: DateTime;
    protected lastInvocationDateTime?: DateTime;

    /**
     * Constructs a Timer object with the specified time interval.
     * @param interval - The time interval for the timer.
     */
    public constructor(interval: TimeSpan) {
        this.interval = interval;
    }

    /**
     * Indicates whether the next timer event should be scheduled before triggering the current one.
     */
    public ScheduleNextBeforeTrigger = false;

    /**
     * Gets the time interval of the timer.
     */
    public get Interval() {
        return this.interval;
    }

    /**
     * Sets the time interval of the timer.
     */
    public set Interval(value: TimeSpan) {
        this.interval = value;
    }

    /**
     * Gets a value indicating whether the timer is currently running.
     */
    public get IsRunning() {
        return this.timeoutID !== undefined;
    }

    /**
     * Gets the date and time when the timer was started.
     */
    public get StartDateTime() {
        return this.startDateTime;
    }

    /**
     * Gets the date and time when the timer was last invoked.
     */
    public get LastInvocationDateTime() {
        return this.lastInvocationDateTime;
    }

    /**
     * Gets the date and time when the next timer event is scheduled to occur.
     */
    public get NextInvocationDateTime() {
        return (this.lastInvocationDateTime ?? this.startDateTime)?.Add(this.interval.TotalMilliseconds);
    }

    /**
     * Gets the number of times the timer has been invoked.
     * This property reflects the total count of elapsed intervals,
     * indicating how many times the timer's 'Elapsed' event has been triggered.
     */
    public get Invocations() {
        return this.index;
    }


    /**
     * Gets an event that is triggered on each elapsed interval of the timer.
     */
    public get Elapsed() {
        return this.elapsed.expose();
    }

    /**
     * Triggers the elapsed event and increments the index.
     * @returns A promise that resolves with the current index.
     */
    protected trigger(): Promise<number> {
        this.index++;
        return this.elapsed.trigger(this.index, this);
    }

    /**
     * Starts the timer.
     * @throws Error if the timer is already running.
     */
    public Start() {
        if (this.IsRunning) {
            throw new Error("Timer is already running.");
        }

        // Schedule the first timer event
        Timer.scheduleNext(this);
        this.startDateTime = new DateTime();
    }

    /**
     * Stops the timer.
     */
    public Stop() {
        clearTimeout(this.timeoutID);
        this.timeoutID = undefined;
    }

    /**
     * Resets the index to zero.
     */
    public Reset() {
        this.index = 0;
        this.startDateTime = undefined;
        this.lastInvocationDateTime = undefined;
    }

    /**
     * Restarts the timer by stopping it, resetting the index, and then starting it again.
     */
    public Restart() {
        if (this.IsRunning) {
            this.Stop();
        }

        this.Reset();
        this.Start();
    }

    /**
     * Schedules the next timer event based on the timer's properties.
     * @param timer - The timer instance.
     */
    protected static scheduleNext(timer: Timer) {
        timer.timeoutID = setTimeout((timer: Timer) => {
            timer.lastInvocationDateTime = new DateTime();
            if (timer.ScheduleNextBeforeTrigger) {
                // Schedule the next event before triggering the current one
                Timer.scheduleNext(timer);
                timer.trigger();
            } else {
                // Trigger the event and then schedule the next one
                timer.trigger().finally(() => Timer.scheduleNext(timer));
            }
        }, timer.interval.TotalMilliseconds, timer);
    }
}
